import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Progress,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { MdArrowBack, MdChevronRight } from "react-icons/md";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PageLayoutWithHeaderNav from "../PageLayoutWithHeaderNav";
import { getManagerCoachingDimension } from "../../services/managerEngagement";

function percentage(value) {
  return value == null ? "—" : `${Math.round(Number(value))}%`;
}

function shortDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

export default function CoachingDimensionDetailPage() {
  const { dimensionType, dimensionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const query = useMemo(() => ({
    dimensionType,
    dimensionId,
    periodId: searchParams.get("period") || undefined,
    jobClass: searchParams.get("job_class") || undefined,
    frameworkFamily: searchParams.get("framework") || undefined,
    reportingGroupId: searchParams.get("reporting_group") || undefined,
    locationId: searchParams.get("location") || undefined,
  }), [dimensionId, dimensionType, searchParams]);

  useEffect(() => {
    let cancelled = false;
    setError("");
    setData(null);
    getManagerCoachingDimension(query)
      .then((response) => { if (!cancelled) setData(response); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || "Could not load this coaching dimension."); });
    return () => { cancelled = true; };
  }, [query]);

  const contextParams = new URLSearchParams(searchParams);
  contextParams.delete("conversation");
  const employeeNames = new Map((data?.employees || []).map((employee) => [employee.id, employee.name]));
  const verdicts = data?.verdict_distribution || {};

  return (
    <PageLayoutWithHeaderNav maxWidth="container.xl">
      <VStack align="stretch" spacing={6}>
        <Button alignSelf="flex-start" variant="ghost" size="sm" leftIcon={<MdArrowBack />} onClick={() => navigate(`/manager/engagement?${contextParams.toString()}`)}>Team coaching</Button>
        {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
        {!error && !data ? <Skeleton h="300px" borderRadius="xl" /> : data ? (
          <>
            <Box>
              <HStack spacing={2} mb={2}><Badge colorScheme="blue">{data.job_class.replace("_", " ")}</Badge><Badge>{data.dimension_type.replace("_", " ")}</Badge></HStack>
              <Heading size="xl">{data.label}</Heading>
              <Text mt={2} color="gray.500">{data.period.label} · {data.framework_family}</Text>
            </Box>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}><Text color="gray.500" fontSize="xs" fontWeight="bold">SCORE</Text><Heading mt={2}>{percentage(data.score)}</Heading>{data.suppressed && <Text mt={1} fontSize="sm" color="orange.600">Hidden until the minimum sample is reached</Text>}</Box>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}><Text color="gray.500" fontSize="xs" fontWeight="bold">COVERAGE</Text><Heading mt={2}>{percentage(data.coverage)}</Heading><Text mt={1} fontSize="sm" color="gray.500">{data.applicable} applicable observations</Text></Box>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}><Text color="gray.500" fontSize="xs" fontWeight="bold">THREADS</Text><Heading mt={2}>{data.scorecard_count}</Heading><Text mt={1} fontSize="sm" color="gray.500">Current reports only</Text></Box>
            </Grid>

            <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={5}>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}>
                <Heading size="md">Trend</Heading>
                <VStack align="stretch" spacing={4} mt={4}>
                  {(data.trend || []).map((point) => (
                    <Box key={point.start_at}>
                      <Flex justify="space-between"><Text fontSize="sm">{shortDate(point.start_at)}–{shortDate(point.end_at)}</Text><Text fontWeight="bold">{percentage(point.score)}</Text></Flex>
                      <Progress mt={2} value={point.score || 0} colorScheme={point.suppressed ? "gray" : "blue"} borderRadius="full" />
                      <Text mt={1} fontSize="xs" color="gray.500">{point.scorecard_count} threads · {point.applicable} applicable</Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
              <Box bg="white" borderWidth="1px" borderRadius="xl" p={5}>
                <Heading size="md">Verdict distribution</Heading>
                <VStack align="stretch" spacing={3} mt={4}>
                  {[['Nailed', 'nailed', 'green'], ['Partial', 'partial', 'yellow'], ['Missed', 'missed', 'red'], ['Not applicable', 'not_applicable', 'gray'], ['Held back', 'held_back', 'purple']].map(([label, key, color]) => (
                    <Flex key={key} justify="space-between" align="center"><Text>{label}</Text><Badge colorScheme={color}>{verdicts[key] || 0}</Badge></Flex>
                  ))}
                </VStack>
              </Box>
            </Grid>

            <Box bg="white" borderWidth="1px" borderRadius="xl" overflowX="auto">
              <Heading size="md" p={5} pb={2}>Contributing employees</Heading>
              <Table size="sm"><Thead><Tr><Th>Employee</Th><Th isNumeric>Threads</Th><Th isNumeric>Score</Th><Th isNumeric>Coverage</Th></Tr></Thead><Tbody>{(data.employees || []).map((employee) => <Tr key={employee.id}><Td>{employee.name}</Td><Td isNumeric>{employee.scorecard_count}</Td><Td isNumeric>{employee.suppressed ? "Suppressed" : percentage(employee.score)}</Td><Td isNumeric>{percentage(employee.coverage)}</Td></Tr>)}</Tbody></Table>
            </Box>

            <Box bg="white" borderWidth="1px" borderRadius="xl" overflow="hidden">
              <Heading size="md" p={5} pb={2}>Source threads</Heading>
              <VStack align="stretch" spacing={0}>
                {(data.threads || []).map((thread) => (
                  <Flex key={thread.thread_id} as="button" type="button" textAlign="left" px={5} py={3} align="center" gap={4} borderTopWidth="1px" _hover={{ bg: "blue.50" }} onClick={() => navigate(`/manager/threads/${thread.thread_id}?${contextParams.toString()}`)}>
                    <Box flex="1"><Text fontWeight="semibold">Thread {thread.thread_id}</Text><Text fontSize="sm" color="gray.500">{employeeNames.get(thread.employee_id) || `Employee ${thread.employee_id}`} · {shortDate(thread.occurred_at)}</Text></Box>
                    <Text fontWeight="bold">{percentage(thread.score)}</Text><MdChevronRight />
                  </Flex>
                ))}
              </VStack>
            </Box>
          </>
        ) : null}
      </VStack>
    </PageLayoutWithHeaderNav>
  );
}
